/**
 * FieldComparison
 *
 * Reusable component for comparing a single field between before/after values.
 * Supports text, number, array, and niveau (chip) types.
 */

import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Paper,
  Grid
} from '@mui/material';
import {
  Edit as EditIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';

// ============================================================================
// TYPES
// ============================================================================

type FieldType = 'text' | 'array' | 'number' | 'niveau';

interface FieldComparisonProps {
  label: string;
  beforeValue: string | number | string[] | undefined;
  afterValue: string | number | string[] | undefined;
  type: FieldType;
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Check if two values are different
 */
function hasChanged(
  before: string | number | string[] | undefined,
  after: string | number | string[] | undefined
): boolean {
  if (Array.isArray(before) && Array.isArray(after)) {
    if (before.length !== after.length) return true;
    return before.some((item, index) => item !== after[index]);
  }
  return before !== after;
}

/**
 * Format value for display
 */
function formatValue(
  value: string | number | string[] | undefined,
  type: FieldType
): string {
  if (value === undefined || value === null) return '(non defini)';
  if (type === 'array' && Array.isArray(value)) {
    return value.length > 0 ? value.join(', ') : '(aucun)';
  }
  if (type === 'number') {
    return `${value} min`;
  }
  return String(value);
}

/**
 * Get niveau chip color
 */
function getNiveauColor(niveau: string): 'success' | 'warning' | 'error' {
  switch (niveau) {
    case 'facile': return 'success';
    case 'moyen': return 'warning';
    case 'difficile': return 'error';
    default: return 'warning';
  }
}

// ============================================================================
// COMPONENT
// ============================================================================

const FieldComparison: React.FC<FieldComparisonProps> = ({
  label,
  beforeValue,
  afterValue,
  type
}) => {
  const changed = hasChanged(beforeValue, afterValue);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 1.5,
        borderColor: changed ? 'warning.main' : 'divider',
        bgcolor: changed ? 'warning.lighter' : 'background.paper'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ flexGrow: 1 }}>
          {label}
        </Typography>
        {changed && (
          <Chip
            icon={<EditIcon fontSize="small" />}
            label="Modifie"
            size="small"
            color="warning"
            variant="outlined"
          />
        )}
      </Box>

      <Grid container spacing={2}>
        {/* Before value */}
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary" display="block">
            Avant
          </Typography>
          {type === 'niveau' ? (
            <Chip
              label={beforeValue || '(non defini)'}
              size="small"
              color={beforeValue ? getNiveauColor(String(beforeValue)) : 'default'}
              sx={{ mt: 0.5 }}
            />
          ) : (
            <Typography
              variant="body2"
              sx={{
                mt: 0.5,
                color: beforeValue ? 'text.primary' : 'text.disabled',
                fontStyle: !beforeValue ? 'italic' : 'normal',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}
            >
              {formatValue(beforeValue, type)}
            </Typography>
          )}
        </Grid>

        {/* Arrow */}
        <Grid item xs={false} sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
          {changed && (
            <RemoveIcon sx={{ color: 'text.disabled', transform: 'rotate(180deg)' }} />
          )}
        </Grid>

        {/* After value */}
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary" display="block">
            Apres
          </Typography>
          {type === 'niveau' ? (
            <Chip
              label={afterValue || '(non defini)'}
              size="small"
              color={afterValue ? getNiveauColor(String(afterValue)) : 'default'}
              sx={{ mt: 0.5 }}
            />
          ) : (
            <Typography
              variant="body2"
              sx={{
                mt: 0.5,
                color: afterValue ? 'text.primary' : 'text.disabled',
                fontStyle: !afterValue ? 'italic' : 'normal',
                fontWeight: changed ? 'medium' : 'normal',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}
            >
              {formatValue(afterValue, type)}
            </Typography>
          )}
        </Grid>
      </Grid>
    </Paper>
  );
};

export default FieldComparison;
